import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useCurrency } from "../context/CurrencyContext";
import { useStatusesStandalone } from "../hooks/useStatuses";
import api, { usersApi, employeesApi, unifiedProjectsApi } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "../components/ui/tooltip";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import { toast } from "sonner";
import { cn } from "../lib/utils";
import { Plus, Search, FolderKanban, ChevronRight, ListTodo, Users, Inbox, UserPlus, Clock, CheckCircle2, XCircle, AlertTriangle, DollarSign, ExternalLink, Trash2, MessageSquare, PlusCircle, Archive, Play, FileSignature } from "lucide-react";
import CreateProjectDialog from "../components/CreateProjectDialog";

const ProjectsPage = () => {
  const { user } = useAuth();
  const { formatAmount } = useCurrency();
  const { projectStatuses, getProjectStatus } = useStatusesStandalone();
  const [projects, setProjects] = useState([]);
  const [salesProjects, setSalesProjects] = useState([]); // Sales projects for Pool
  const [pmManagers, setPmManagers] = useState([]); // PM managers for assignment
  const [salesManagers, setSalesManagers] = useState([]); // Sales managers for filter
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [pmFilter, setPmFilter] = useState("all");
  const [salesFilter, setSalesFilter] = useState("all");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewTab, setViewTab] = useState("active"); // "active" or "archive"
  const [poolProjects, setPoolProjects] = useState([]); // Projects from PM Pool API
  const [priceRequests, setPriceRequests] = useState([]); // Price approval requests
  const [employeeToUserMap, setEmployeeToUserMap] = useState({}); // Map employee ID to user IDs (by email)
  
  // Rejection dialog state
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectingRequestId, setRejectingRequestId] = useState(null);
  const [rejectReason, setRejectReason] = useState("");
  
  // Helper function for rounding prices
  const formatPrice = (value) => {
    if (value === null || value === undefined) return "0";
    const num = parseFloat(value);
    if (isNaN(num)) return "0";
    // Round to max 1 decimal place
    return num.toLocaleString(undefined, { maximumFractionDigits: 1 });
  };
  
  // Helper function to get correct project URL based on user role
  // All roles go to /unified-projects/:id (SalesProjectEditPage)
  const getProjectUrl = (projectId) => {
    return `/unified-projects/${projectId}`;
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [productionProjectsRes, clientsRes, unifiedSalesRes, empRes, salesClientsRes, poolRes, priceReqRes, allUsersRes] = await Promise.all([
        unifiedProjectsApi.getAll({ phase: "production" }), // Production projects from unified API
        (user?.role === "admin" || user?.role === "pm_lead") ? usersApi.getAll("client") : Promise.resolve({ data: [] }),
        unifiedProjectsApi.getAll({ phase: "sales" }), // Sales projects for Pool
        employeesApi.getAll().catch(() => ({ data: [] })),
        api.get("/sales-clients").catch(() => ({ data: [] })), // Get sales clients for combobox
        api.get("/pm-pool").catch(() => ({ data: [] })), // Get PM Pool projects (includes TRP)
        api.get("/price-requests").catch(() => ({ data: [] })), // Price approval requests
        usersApi.getAll().catch(() => ({ data: [] })), // Get all portal users for employee-user mapping
      ]);
      setProjects(productionProjectsRes.data || []); // Now from unified API
      setClients(salesClientsRes.data || []);
      setSalesProjects(unifiedSalesRes.data || []);
      setPoolProjects(poolRes.data || []);
      setPriceRequests(priceReqRes.data || []);
      
      // Get all employees
      const employees = empRes.data || [];
      const allUsers = allUsersRes.data || [];
      
      // Create employee to user ID mapping (by email)
      // This allows filtering to work when project_manager_id is either employee ID or user ID
      const empToUserMap = {};
      employees.forEach(emp => {
        const matchingUser = allUsers.find(u => u.email === emp.email);
        if (matchingUser) {
          // Map employee ID to both IDs (employee and user)
          empToUserMap[emp.id] = [emp.id, matchingUser.id];
        } else {
          empToUserMap[emp.id] = [emp.id];
        }
      });
      setEmployeeToUserMap(empToUserMap);
      
      // Get PM managers (project_manager position)
      const pms = employees.filter(e => 
        (e.position === "project_manager" || e.position === "admin") && e.is_active
      );
      setPmManagers(pms);
      
      // Get Sales managers
      const sales = employees.filter(e => 
        (e.position === "sales_manager" || e.position === "admin") && e.is_active
      );
      setSalesManagers(sales);
    } catch (error) {
      console.error("Error fetching projects:", error);
      toast.error("Failed to load projects");
    } finally {
      setLoading(false);
    }
  };

  // Price Request handlers
  const handleApproveRequest = async (requestId) => {
    try {
      await api.put(`/price-requests/${requestId}`, { action: "approve" });
      toast.success("Request approved - Tasks created");
      fetchData();
    } catch (error) {
      toast.error("Failed to approve");
    }
  };

  // Open rejection dialog instead of prompt
  const handleRejectRequest = (requestId) => {
    setRejectingRequestId(requestId);
    setRejectReason("");
    setRejectDialogOpen(true);
  };
  
  // Confirm rejection
  const confirmReject = async () => {
    if (!rejectingRequestId) return;
    try {
      await api.put(`/price-requests/${rejectingRequestId}`, { action: "reject", reason: rejectReason });
      toast.success("Request rejected");
      setRejectDialogOpen(false);
      setRejectingRequestId(null);
      setRejectReason("");
      fetchData();
    } catch (error) {
      toast.error("Failed to reject");
    }
  };
  
  // Delete rejected request
  const handleDeleteRequest = async (requestId) => {
    try {
      await api.delete(`/price-requests/${requestId}`);
      toast.success("Request deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete request");
    }
  };
  
  // Create task from approved request
  const handleCreateTaskFromRequest = async (request) => {
    try {
      await api.post(`/price-requests/${request.id}/create-task`);
      toast.success("Task created from request");
      fetchData();
    } catch (error) {
      toast.error("Failed to create task");
    }
  };

  // Pool projects are now fetched directly from /pm-pool API (includes TRP projects)
  
  // My sales projects (assigned to current user as PM)
  const mySalesProjects = salesProjects.filter(p => {
    // Find current user's employee ID
    const myEmp = pmManagers.find(m => m.email === user?.email);
    return myEmp && p.project_manager_id === myEmp.id;
  });

  // Handle taking a project from pool - use new PM pool API
  const handleTakeProject = async (projectId) => {
    try {
      await unifiedProjectsApi.takeFromPool(projectId);
      toast.success("Project assigned to you!");
      fetchData();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail ? `Failed to take project: ${detail}` : "Failed to take project");
    }
  };

  // Handle assigning project to another PM (for pm_lead). Uses the same
  // /pm-pool/:id/take endpoint as self-take but with ?pm_id= so it bypasses
  // the unified-projects required-fields validation and works for TRP too.
  const handleAssignProject = async (projectId, pmId) => {
    try {
      await api.post(`/pm-pool/${projectId}/take`, null, { params: { pm_id: pmId } });
      toast.success("Project assigned!");
      fetchData();
    } catch (error) {
      const detail = error?.response?.data?.detail;
      toast.error(detail ? `Failed to assign project: ${detail}` : "Failed to assign project");
    }
  };

  // ── "Signed" watchlist ────────────────────────────────────────────────
  // Contract is signed but the money hasn't landed yet, so the project is not
  // in the pool. pm_lead asked to see these right under the pool: they are the
  // queue behind it — each one drops into the pool the moment payment clears.
  // backlog f9eca60f
  const poolIds = new Set(poolProjects.map(p => p.id));
  const signedProjects = [...salesProjects, ...projects]
    .filter(p => p.status === "contract_signed" && !poolIds.has(p.id))
    // sales + production lists can overlap on a project mid-handoff
    .filter((p, i, arr) => arr.findIndex(x => x.id === p.id) === i)
    .map(p => {
      const total = Number(p.total_amount) || Number(p.revenue) || Number(p.budget) || 0;
      const paid = Number(p.paid_revenue) || 0;
      return { ...p, _total: total, _paid: paid, _outstanding: Math.max(total - paid, 0) };
    })
    // Biggest unpaid balance first — that's the one worth chasing.
    .sort((a, b) => b._outstanding - a._outstanding);

  // Unique statuses for filter
  const uniqueStatuses = [...new Set(projects.map(p => p.status).filter(Boolean))];

  const ARCHIVE_STATUSES = ["completed", "archived"];
  
  const filteredProjects = projects.filter((p) => {
    // Tab filter
    const isArchived = ARCHIVE_STATUSES.includes(p.status) || p.phase === "completed";
    if (viewTab === "active" && isArchived) return false;
    if (viewTab === "archive" && !isArchived) return false;
    
    const matchesSearch = p.name?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === "all" || p.status === statusFilter;
    // Check if project_manager_id matches either employee ID or user ID for selected PM
    const pmIds = pmFilter === "all" ? null : (employeeToUserMap[pmFilter] || [pmFilter]);
    const matchesPM = pmFilter === "all" || (pmIds && pmIds.includes(p.project_manager_id));
    // Same logic for sales filter
    const salesIds = salesFilter === "all" ? null : (employeeToUserMap[salesFilter] || [salesFilter]);
    const matchesSales = salesFilter === "all" || (salesIds && salesIds.includes(p.sales_manager_id));
    return matchesSearch && matchesStatus && matchesPM && matchesSales;
  });
  
  const activeCount = projects.filter(p => !ARCHIVE_STATUSES.includes(p.status) && p.phase !== "completed").length;
  const archiveCount = projects.filter(p => ARCHIVE_STATUSES.includes(p.status) || p.phase === "completed").length;

  const getStatusBadge = (status) => {
    const statusConfig = getProjectStatus(status);
    return (
      <Badge variant="outline" className={cn("text-xs", statusConfig.color)}>
        {statusConfig.name || status?.replace(/_/g, ' ')}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-orange-500" />
      </div>
    );
  }

  return (
    <div data-testid="projects-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-zinc-50 font-['Manrope']">Projects</h1>
          <p className="text-sm text-zinc-500">{filteredProjects.length} projects</p>
        </div>

        {(user?.role === "admin" || user?.role === "pm_lead") && (
          <Button 
            onClick={() => setDialogOpen(true)}
            className="bg-orange-500 hover:bg-orange-600 text-white" 
            data-testid="create-project-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            New Project
          </Button>
        )}
      </div>

      {/* Create Project Dialog */}
      <CreateProjectDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        onSuccess={fetchData}
        defaultPhase="production"
      />

      {/* Price Approval Requests from TRP */}
      {(user?.role === "admin" || user?.role === "pm_lead") && priceRequests.length > 0 && (
        <Card className="bg-purple-500/5 border-purple-500/20 mb-6">
          <CardHeader className="pb-2">
            <CardTitle className="text-purple-400 flex items-center gap-2 text-base">
              <DollarSign className="h-5 w-5" />
              Price Approval Requests
              <Badge className="bg-purple-500/20 text-purple-400 border-purple-500/30">{priceRequests.length}</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Group by project */}
            {Object.entries(
              priceRequests.reduce((acc, req) => {
                const key = req.project_name || "Unknown";
                if (!acc[key]) acc[key] = { requests: [], total: 0, project_id: req.project_id };
                acc[key].requests.push(req);
                acc[key].total += (req.proposed_price || 0) * (req.quantity || 1);
                return acc;
              }, {})
            ).map(([projectName, { requests, total, project_id }]) => (
              <div key={projectName} className="mb-4 last:mb-0">
                <div className="flex items-center justify-between mb-2">
                  <Link 
                    to={`/trp/project/${project_id}`}
                    className="text-zinc-50 font-medium hover:text-purple-400 transition-colors flex items-center gap-1.5"
                  >
                    {projectName}
                    <ExternalLink className="h-3 w-3" />
                  </Link>
                  <span className="text-sm text-zinc-400">Total: ${formatPrice(total)}</span>
                </div>
                <Table>
                  <TableHeader>
                    <TableRow className="border-zinc-800">
                      <TableHead className="text-zinc-400 text-xs">Task</TableHead>
                      <TableHead className="text-zinc-400 text-xs">Project</TableHead>
                      <TableHead className="text-zinc-400 text-xs">Type</TableHead>
                      <TableHead className="text-zinc-400 text-xs text-right">Base</TableHead>
                      <TableHead className="text-zinc-400 text-xs text-right">Proposed</TableHead>
                      <TableHead className="text-zinc-400 text-xs text-right">Margin</TableHead>
                      <TableHead className="text-zinc-400 text-xs text-center">Qty</TableHead>
                      <TableHead className="text-zinc-400 text-xs text-right">Total</TableHead>
                      <TableHead className="text-zinc-400 text-xs">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {requests.map((req) => {
                      const margin = req.base_price > 0 
                        ? ((req.proposed_price - req.base_price) / req.base_price * 100).toFixed(1)
                        : 0;
                      const isLow = req.proposed_price < req.base_price;
                      const qty = req.quantity || 1;
                      const totalPrice = (req.proposed_price || 0) * qty;
                      const hasDiscount = req.discount_percent && req.discount_percent > 0;
                      
                      return (
                        <TableRow key={req.id} className="border-zinc-800">
                          <TableCell>
                            <span className="text-zinc-50 text-sm">{req.task_name}</span>
                          </TableCell>
                          <TableCell>
                            <Link 
                              to={getProjectUrl(req.project_id)} 
                              className="text-purple-400 text-sm hover:text-purple-300 transition-colors underline"
                              title="View project"
                            >
                              {req.project_name || "Unknown"}
                            </Link>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline" className="text-xs bg-zinc-800 text-zinc-300">
                              {req.task_type}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right text-zinc-400 font-mono text-sm">
                            ${formatPrice(req.base_price)}
                          </TableCell>
                          <TableCell className={cn(
                            "text-right font-mono text-sm font-medium",
                            isLow ? "text-red-400" : "text-emerald-400"
                          )}>
                            <div className="flex items-center justify-end gap-1">
                              ${formatPrice(req.proposed_price)}
                              {isLow && <AlertTriangle className="h-3 w-3" />}
                              {hasDiscount && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger>
                                      <MessageSquare className="h-3 w-3 text-blue-400" />
                                    </TooltipTrigger>
                                    <TooltipContent className="bg-zinc-800 border-zinc-700 max-w-xs">
                                      <p className="text-xs text-zinc-50">
                                        <span className="text-blue-400 font-medium">Discount reason:</span>
                                        <br />
                                        {req.discount_reason || "No reason provided"}
                                      </p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className={cn(
                            "text-right font-mono text-sm",
                            parseFloat(margin) >= 0 ? "text-emerald-400" : "text-red-400"
                          )}>
                            {margin}%
                          </TableCell>
                          <TableCell className="text-center text-zinc-400 text-sm">
                            {qty}
                          </TableCell>
                          <TableCell className="text-right font-mono text-sm font-bold text-zinc-50">
                            ${formatPrice(totalPrice)}
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1">
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 px-2 text-xs text-emerald-400 hover:text-emerald-300 hover:bg-emerald-500/10"
                                onClick={() => handleApproveRequest(req.id)}
                              >
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                OK
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 px-2 text-xs text-red-400 hover:text-red-300 hover:bg-red-500/10"
                                onClick={() => handleRejectRequest(req.id)}
                              >
                                <XCircle className="h-3 w-3" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Pool Section - Sales projects without PM */}
      {(user?.role === "pm_lead" || user?.role === "team" || user?.role === "admin") && poolProjects.length > 0 && (
        <Card className="bg-orange-500/5 border-orange-500/20 mb-6">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Inbox className="h-5 w-5 text-orange-400" />
              <h3 className="text-orange-400 font-semibold">Project Pool</h3>
              <Badge className="bg-orange-500/20 text-orange-400 border-orange-500/30">{poolProjects.length}</Badge>
              <span className="text-xs text-zinc-500 ml-2">Projects waiting for PM assignment</span>
            </div>
            <div className="space-y-2">
              {poolProjects.slice(0, 10).map(project => (
                <div key={project.id} className="flex items-center justify-between bg-zinc-900/50 rounded-lg p-3 border border-zinc-800 group">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <Link 
                        to={getProjectUrl(project.id)} 
                        className="text-zinc-50 font-medium hover:text-orange-400 transition-colors"
                        title="View project details"
                      >
                        {project.name || project.client_name}
                      </Link>
                      {project.source === "trp" && (
                        <Badge variant="outline" className="text-xs bg-purple-500/20 text-purple-400 border-purple-500/30">
                          TRP
                        </Badge>
                      )}
                      <Badge variant="outline" className="text-xs bg-zinc-800 text-zinc-400 border-zinc-700">
                        {project.status?.replace(/_/g, ' ')}
                      </Badge>
                      <Link 
                        to={getProjectUrl(project.id)}
                        className="text-zinc-500 hover:text-orange-400 transition-colors"
                        title="View project"
                      >
                        <ExternalLink className="h-3.5 w-3.5" />
                      </Link>
                    </div>
                    <div className="flex items-center gap-4 text-xs text-zinc-500 mt-1">
                      <span>Client: {project.client_name}</span>
                      {project.task_count > 0 && (
                        <span className="text-zinc-400">{project.task_count} tasks</span>
                      )}
                      <span className="text-green-400">
                        {formatAmount(project.total_amount || project.budget || 0, project.currency || "USD")}
                      </span>
                      {project.services_count > 0 && (
                        <span className="text-zinc-400">{project.services_count} services</span>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {user?.role === "pm_lead" || user?.role === "admin" ? (
                      <Select onValueChange={(pmId) => handleAssignProject(project.id, pmId)}>
                        <SelectTrigger className="w-40 h-8 bg-zinc-800 border-zinc-700 text-xs">
                          <SelectValue placeholder="Assign to PM..." />
                        </SelectTrigger>
                        <SelectContent className="bg-zinc-800 border-zinc-700">
                          {pmManagers.map(pm => (
                            <SelectItem key={pm.id} value={pm.id} className="text-xs">{pm.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Button 
                        size="sm" 
                        onClick={() => handleTakeProject(project.id)}
                        className="bg-orange-500 hover:bg-orange-600 text-white h-8"
                      >
                        <UserPlus className="h-3 w-3 mr-1" />
                        Take Project
                      </Button>
                    )}
                  </div>
                </div>
              ))}
              {poolProjects.length > 10 && (
                <p className="text-xs text-zinc-500 text-center pt-2">
                  And {poolProjects.length - 10} more projects in pool...
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Signed — contract done, waiting on payment. Sits directly under the
          pool because it IS the queue behind the pool. backlog f9eca60f */}
      {(user?.role === "pm_lead" || user?.role === "team" || user?.role === "admin") && signedProjects.length > 0 && (
        <Card className="bg-emerald-500/5 border-emerald-500/20 mb-6" data-testid="signed-awaiting-payment">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <FileSignature className="h-5 w-5 text-emerald-400" />
              <h3 className="text-emerald-400 font-semibold">Signed</h3>
              <Badge className="bg-emerald-500/20 text-emerald-400 border-emerald-500/30">{signedProjects.length}</Badge>
              <span className="text-xs text-zinc-500 ml-2">Contract signed — waiting for payment before they enter the pool</span>
            </div>
            <div className="space-y-2">
              {signedProjects.slice(0, 10).map(project => {
                const partlyPaid = project._paid > 0 && project._outstanding > 0;
                return (
                  <div key={project.id} className="flex items-center justify-between gap-3 bg-zinc-900/50 rounded-lg p-3 border border-zinc-800">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <Link
                          to={getProjectUrl(project.id)}
                          className="text-zinc-50 font-medium hover:text-emerald-400 transition-colors truncate"
                          title="View project details"
                        >
                          {project.name || project.client_name}
                        </Link>
                        {getStatusBadge(project.status)}
                        {project.project_manager_name && (
                          <Badge variant="outline" className="text-xs bg-zinc-800 text-zinc-400 border-zinc-700">
                            PM: {project.project_manager_name}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-4 text-xs text-zinc-500 mt-1">
                        <span>Client: {project.client_name || "—"}</span>
                        {project.start_date && <span>Start: {project.start_date}</span>}
                        {project.services_count > 0 && <span>{project.services_count} services</span>}
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <div className="text-sm font-medium text-amber-400" title="Still unpaid">
                        {formatAmount(project._outstanding, project.currency || "USD")}
                      </div>
                      <div className="text-[11px] text-zinc-500">
                        {partlyPaid
                          ? `${formatAmount(project._paid, project.currency || "USD")} of ${formatAmount(project._total, project.currency || "USD")} paid`
                          : `of ${formatAmount(project._total, project.currency || "USD")} · nothing paid`}
                      </div>
                    </div>
                  </div>
                );
              })}
              {signedProjects.length > 10 && (
                <p className="text-xs text-zinc-500 text-center pt-2">
                  And {signedProjects.length - 10} more signed projects...
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4 items-center">
        {/* Minimal Active/Archive toggle */}
        <div className="flex items-center gap-0.5 mr-2 border-b border-zinc-800" data-testid="pm-project-view-tabs">
          <button
            onClick={() => setViewTab("active")}
            className={cn("px-3 py-1.5 text-xs font-medium transition-colors border-b-2 -mb-px",
              viewTab === "active" ? "text-orange-400 border-orange-400" : "text-zinc-500 border-transparent hover:text-zinc-300")}
            data-testid="pm-tab-active"
          >
            Active {activeCount}
          </button>
          <button
            onClick={() => setViewTab("archive")}
            className={cn("px-3 py-1.5 text-xs font-medium transition-colors border-b-2 -mb-px",
              viewTab === "archive" ? "text-zinc-300 border-zinc-400" : "text-zinc-500 border-transparent hover:text-zinc-300")}
            data-testid="pm-tab-archive"
          >
            Archive {archiveCount}
          </button>
        </div>
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-zinc-500" />
          <Input
            placeholder="Search projects..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 bg-zinc-900 border-zinc-800 text-zinc-50 placeholder:text-zinc-600 focus:border-orange-500 h-9"
            data-testid="search-projects"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[140px] bg-zinc-900 border-zinc-800 text-zinc-50 h-9" data-testid="status-filter">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all" className="text-zinc-300">All Status</SelectItem>
            {uniqueStatuses.map(s => (
              <SelectItem key={s} value={s} className="text-zinc-300">
                {getProjectStatus(s)?.name || s?.replace(/_/g, ' ')}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={pmFilter} onValueChange={setPmFilter}>
          <SelectTrigger className="w-[150px] bg-zinc-900 border-zinc-800 text-zinc-50 h-9">
            <SelectValue placeholder="PM" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all" className="text-zinc-300">All PMs</SelectItem>
            {pmManagers.map(m => (
              <SelectItem key={m.id} value={m.id} className="text-zinc-300">{m.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={salesFilter} onValueChange={setSalesFilter}>
          <SelectTrigger className="w-[150px] bg-zinc-900 border-zinc-800 text-zinc-50 h-9">
            <SelectValue placeholder="Sales" />
          </SelectTrigger>
          <SelectContent className="bg-zinc-900 border-zinc-800">
            <SelectItem value="all" className="text-zinc-300">All Sales</SelectItem>
            {salesManagers.map(m => (
              <SelectItem key={m.id} value={m.id} className="text-zinc-300">{m.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Projects Table */}
      {filteredProjects.length === 0 ? (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <FolderKanban className="h-10 w-10 text-zinc-600 mb-3" />
            <p className="text-zinc-500">No projects found</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="bg-zinc-900/50 border-zinc-800 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="border-zinc-800 hover:bg-transparent">
                <TableHead className="text-zinc-400 font-medium w-12">#</TableHead>
                <TableHead className="text-zinc-400 font-medium">Project Name</TableHead>
                <TableHead className="text-zinc-400 font-medium">Client</TableHead>
                <TableHead className="text-zinc-400 font-medium text-center">Tasks</TableHead>
                <TableHead className="text-zinc-400 font-medium text-right">Revenue</TableHead>
                <TableHead className="text-zinc-400 font-medium text-right">Costs</TableHead>
                <TableHead className="text-zinc-400 font-medium text-right">Margin</TableHead>
                <TableHead className="text-zinc-400 font-medium text-center">Status</TableHead>
                <TableHead className="text-zinc-400 font-medium w-10"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredProjects.map((project, idx) => {
                // Calculate margin for PM projects
                const projectCurrency = project.currency || "USD";
                // Revenue/Costs/Profit - use display values in project currency
                const revenue = project.revenue_display || project.revenue || project.total_amount || project.budget || 0;
                const costs = project.costs_display || project.costs || project.total_costs || project.spent || 0;
                const profit = project.profit_display || (revenue - costs);
                const hasCosts = costs > 0;
                const marginPercent = revenue > 0 && hasCosts ? (profit / revenue) * 100 : 0;
                const marginColor = !hasCosts ? "text-amber-400" :
                  marginPercent >= 50 ? "text-green-400" : 
                  marginPercent >= 30 ? "text-yellow-400" : "text-red-400";
                
                return (
                <TableRow
                  key={project.id}
                  className="border-zinc-800 hover:bg-zinc-800/50 cursor-pointer group"
                  data-testid={`project-row-${project.id}`}
                >
                  <TableCell className="font-mono text-zinc-500 text-sm">
                    {String(idx + 1).padStart(2, '0')}
                  </TableCell>
                  <TableCell>
                    <Link to={getProjectUrl(project.id)} className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded bg-zinc-800 flex items-center justify-center flex-shrink-0">
                        <FolderKanban className="h-4 w-4 text-orange-500" />
                      </div>
                      <div>
                        <p className="text-zinc-50 font-medium group-hover:text-orange-500 transition-colors">
                          {project.name}
                        </p>
                        {project.description && (
                          <p className="text-xs text-zinc-500 truncate max-w-[200px]">{project.description}</p>
                        )}
                      </div>
                    </Link>
                  </TableCell>
                  <TableCell className="text-zinc-400 text-sm">
                    {project.client_name || <span className="text-zinc-600">—</span>}
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center gap-1.5">
                      <ListTodo className="h-3.5 w-3.5 text-zinc-500" />
                      <span className="text-zinc-50 font-medium">{project.task_count}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-mono text-zinc-300 text-sm">
                    {formatAmount(revenue, projectCurrency)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-sm text-zinc-400">
                    {formatAmount(costs, projectCurrency)}
                  </TableCell>
                  <TableCell className="text-right">
                    {revenue > 0 ? (
                      hasCosts ? (
                        <div className="text-right">
                          <span className={cn("font-mono text-sm font-medium", marginColor)}>
                            {marginPercent.toFixed(0)}%
                          </span>
                          <p className="text-[10px] text-zinc-500">
                            {formatAmount(profit, projectCurrency)}
                          </p>
                        </div>
                      ) : (
                        <span className="text-amber-400 text-xs font-medium">— (add costs)</span>
                      )
                    ) : (
                      <span className="text-zinc-500 text-sm">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-center">
                    {getStatusBadge(project.status)}
                  </TableCell>
                  <TableCell>
                    <Link to={getProjectUrl(project.id)}>
                      <ChevronRight className="h-4 w-4 text-zinc-600 group-hover:text-orange-500 transition-colors" />
                    </Link>
                  </TableCell>
                </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </Card>
      )}
      
      {/* Rejection Dialog */}
      <Dialog open={rejectDialogOpen} onOpenChange={setRejectDialogOpen}>
        <DialogContent className="bg-zinc-900 border-zinc-800 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-zinc-50">Reject Price Request</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Label className="text-zinc-400 text-sm">Rejection reason (optional)</Label>
            <Textarea
              value={rejectReason}
              onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter reason for rejection..."
              className="mt-2 bg-zinc-950 border-zinc-700 text-zinc-50"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRejectDialogOpen(false)}
              className="border-zinc-700"
            >
              Cancel
            </Button>
            <Button
              onClick={confirmReject}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Reject Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ProjectsPage;
